import { useEffect, useRef, useState } from "react"

import { App, Modal, Space, Spin, Typography } from "antd"
import cls from "classname"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"

import ExternallyConnectPaymentProcessorForm from "../../Components/Forms/ExternallyConnectPaymentProcessorForm"
import AcceptInviteModal from "../../Components/Modal/AcceptInviteModal"
import { PrivateRoute } from "../../Components/PrivateRoute"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { usePhoneCountries } from "../../hooks/usePhoneCountries"
import Icon from "../../Icon"
import { defaultInnerPage } from "../../Sagas/StartupSagas"
import { modalConfig } from "../../theme"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { adminRoleId, highAccessRoles, superAdminRoleId } from "../../Utils/permission"
import utils, {
  createAccountStepPath,
  onOkShowMessage,
  paymentProcessorMap
} from "../../Utils/utils"
import AdminInvoicesPage from "../AdminInvoicesPage"
import AdminUsersPage from "../AdminUsersPage"
import AlertsPage from "../AlertsPage"
import { AlertDetailsPage } from "../AlertsPage/alertDetailsPage"
import BusinessAccountsPage from "../BusinessAccountsPage"
import BusinessSettingsPage from "../BusinessSettings"
import AdminEnrollments from "../BusinessSettings/AdminEnrollments"
import Business from "../BusinessSettings/Business"
import Pricing from "../BusinessSettings/Pricing"
import { UserBalancePageForAdmin } from "../BusinessSettings/UserBalancePageForAdmin"
import ConfigurePage from "../ConfigurePage"
import AutoRefundPage from "../ConfigurePage/AutoRefund/AutoRefundPage"
import ConnectShopifyStoreVerifyPage from "../ConnectShopifyStoreVerifyPage"
import CreditsPage from "../CreditsPage"
import DashboardPage from "../DashboardPage"
import AlertsByBankPage from "../DashboardPage/subPage/AlertsByBank"
import AlertsByCountryPage from "../DashboardPage/subPage/AlertsByCountry"
import UsersBySourcePage from "../DashboardPage/subPage/UsersBySource"
import DigitalReceiptPage from "../DigitalReceiptPage"
import GettingStartedPage from "../GettingStartedPage"
import CountryOlympicsGuard from "../Health/components/CountryOlympicsGuard"
import HealthMonitoringPage from "../Health/HealthMonitoring"
import HealthOverviewPage from "../Health/HealthOverview"
import MastercardEstimatedProgramStatusPage from "../Health/Mastercard/components/EstimatedProgramStatus/MastercardEstimatedProgramStatusPage"
import BreakdownByCountryPage from "../Health/subPage/BreakdownByCountryPage"
import BreakdownByReasonCodePage from "../Health/subPage/BreakdownByReasonCodePage"
import ChargebackOlympicsPage from "../Health/subPage/ChargebackOlympicsPage"
import EstimatedProgramStatusPage from "../Health/VAMP/components/EstimatedProgramStatus/EstimatedProgramStatusPage"
import IntegrationsPage from "../IntegrationsPage"
import CustomLayout from "../Layout"
import LayoutUserPageForAdmin from "../LayoutUserPageForAdmin"
import AuthActions from "../LoginPage/reducer"
import MarketingPage from "../MarketingPage"
import NotFoundPage from "../NotFoundPage"
import PaymentMatchingPage from "../PaymentMatchingPage"
import PerksAdminPage from "../PerksAdminPage"
import PerksPage from "../PerksPage"
import PersonalSettingsPage from "../PersonalSettings"
import EndpointDetailsPage from "../PersonalSettings/PublicAPI/components/EndpointDetailsPage"
import EventDetailsPage from "../PersonalSettings/PublicAPI/components/EventDetailsPage"
import PublicAPI from "../PersonalSettings/PublicAPI/PublicAPI"
import Users from "../PersonalSettings/Users"
import PricingPage from "../PricingPage"
import Referrals from "../Referrals"
import EarningsAdminPage from "../Referrals/EarningsAdminPage"
import ReferralsOutsidersLayout from "../Referrals/Outsiders/layout"
import ReferralBAPage from "../Referrals/ReferralBAPage"
import RequestsPage from "../RequestsPage"
import SecurityPage from "../SecurityPage"
import TeamPage from "../TeamPage"
import UserActions from "../User/reducer"
import UsersPage from "../UsersPage"

const { Text } = Typography
const appType = process.env.REACT_APP_TYPE

function InternalPages(props) {
  const {
    auth,
    userInfo,
    isAdmin,
    isMobile,
    businessAccounts,
    businessAccountsFetching,
    businessAccountsWithRole,
    demoTourStep,
    saveFetching,
    saveResult,
    logout
  } = props

  const { t } = useTranslation()
  const { notification, modal } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  const externalDemoMode = useExternalDemoMode()
  const demoRef = useRef(null)
  const helpRef = useRef({ firstLoading: true, firstUserInfoFetched: false })
  const [connectPayment, setConnectPayment] = useState(null)
  usePhoneCountries()
  const connectPaymentName = paymentProcessorMap[connectPayment?.["service_name"]] ?? ""

  let isAuthUserPages = !!auth?.accessToken && !isAdmin
  const isAuthReferralOutsider = isAuthUserPages && userInfo?.referral_outsider
  const isAuthStandardUser = isAuthUserPages && !userInfo?.referral_outsider
  let isAuthAdminPages = !!auth?.accessToken && isAdmin
  const isRestrictedInvoicesAdmin = userInfo?.role?.id === 5

  const isHighAccessRoleBA = !userInfo?.ba_role || highAccessRoles?.includes(userInfo?.ba_role?.id)
  const isSuperAdmin = userInfo?.role?.id === superAdminRoleId
  const isCommonAdmin = isAdmin && userInfo?.role?.id === adminRoleId
  const isAdminPartner = isAdmin && userInfo?.role?.id === 5

  const isOpenOnboarding = !isOnboardingFinished(userInfo) && !userInfo?.is_created_by_invitation

  let filteredBusinessAccounts = [...businessAccounts]?.filter(
    (ba) => !ba?.["connected_payment_processors"]?.includes(connectPayment?.service_name)
  )

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    if (isAuthReferralOutsider && appType !== "partner") {
      logout()
    }
    if (isAuthStandardUser && appType === "partner") {
      logout()
    }
    if (isAuthAdminPages && appType === "partner") {
      logout()
    }
  }, [isAuthStandardUser, isAuthReferralOutsider, isAuthAdminPages])

  useEffect(() => {
    if (userInfo?.["externally_connect_processor"] && helpRef.current.firstUserInfoFetched) {
      setConnectPayment(userInfo?.["externally_connect_processor"]?.[0])
    }
    helpRef.current.firstUserInfoFetched = true
    if (userInfo?.message && helpRef.current.firstLoading) showMessage(userInfo?.message)
  }, [userInfo])

  useEffect(() => {
    if (userInfo?.id) props.getMaxCreateSteps()
  }, [userInfo?.id])

  useEffect(() => {
    if (["connected", "disconnected"]?.includes(saveResult)) {
      if (connectPayment) {
        let namePayment =
          paymentProcessorMap?.[connectPayment.service_name] ||
          utils.toUpperLatter(connectPayment.service_name)
        let message = null
        let description = null
        if (saveResult === "connected") {
          message = t("Payment processor connected")
          description = t("_namePayment_ was connected successfully.", { namePayment })
        } else if (saveResult === "disconnected") {
          message = t("Payment processor disconnected")
          description = t("_namePayment_ was disconnected successfully.", { namePayment })
        }
        notification.success({
          message,
          description,
          ...notificationConfig
        })
        handleCloseModal()
        props.userSaveResultReset()
      }
    }
  }, [saveResult])

  // Tour guide reset on reload
  useEffect(() => {
    if (demoTourStep && userInfo?.is_demo && !userInfo?.is_light_mode) {
      props.setDemoTourStep(1)
      navigate("/dashboard")
    } else {
      props.setDemoTourStep(null)
    }
  }, [])

  const disconnectPayment = (payment) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: t("You have unsaved progress"),
      content: t(
        "Are you sure you want to stop connecting the payment processor? You will lose all your progress and have to start connecting process from scratch."
      ),

      cancelText: t("Cancel"),
      okText: t("Confirm"),
      okButtonProps: {
        ...modalConfig("exclamation")?.okButtonProps,
        danger: true,
        ghost: true
      },
      onOk() {
        props.disconnectPaymentProcessor({
          service_name: payment.service_name,
          ...payment?.["request_params"]
        })
      }
    })
  }

  const showMessage = (message) => {
    if (helpRef.current.firstLoading) helpRef.current.firstLoading = false
    modal.warning({
      ...modalConfig("info"),
      centered: !isMobile,

      title: message?.title,
      content: message?.text,

      okText: t("Got it"),
      okButtonProps: {
        ...modalConfig("exclamation")?.okButtonProps,
        style: { width: "auto" }
      },
      onOk() {
        onOkShowMessage[message.title] &&
          props?.[onOkShowMessage[message.title].action](onOkShowMessage[message.title].value)
        props.clearMessage()
      }
    })
  }

  const handleCloseModal = () => {
    setConnectPayment(null)
    if (saveResult?.["connect_error"]) props.userSaveResultReset()
  }
  const handleCancel = () => {
    disconnectPayment(connectPayment)
  }

  const handleAddNewAccount = () => {
    props.userSaveResultReset()
    props.changeNextCreateAccountStep(1)
    props.setRedirectAfterCreateAccount(location.pathname)
    navigate(createAccountStepPath[1])
  }

  const handleSubmit = (formData) => {
    if (filteredBusinessAccounts.length) {
      const data = {
        ...formData,
        service_name: connectPayment.service_name,
        ...connectPayment?.["request_params"]
      }
      props.connectPaymentProcessor(data)
    } else {
      setConnectPayment(null)
      if (userInfo?.new_onboarding_finished) {
        handleAddNewAccount()
      }
    }
  }

  if (appType === "partner")
    return (
      <Routes>
        <Route element={<PrivateRoute redirectPath="/login" auth={isAuthReferralOutsider} />}>
          <Route element={<ReferralsOutsidersLayout />}>
            <Route path="/referrals" element={<Referrals />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    )

  return (
    <>
      <Routes>
        {/* === USER PAGES === */}
        <Route element={<PrivateRoute redirectPath="/login" auth={isAuthStandardUser} />}>
          <Route element={<CustomLayout />}>
            <Route path="integrations/:page" element={<IntegrationsPage />} />
            <Route
              path="integrations"
              element={<Navigate to="/integrations/processors" replace />}
            />
            <Route path="developers" element={<PublicAPI />} />
            <Route path="developers/:hash" element={<PublicAPI />} />
            <Route path="developers/webhooks/endpoint/details" element={<EndpointDetailsPage />} />
            <Route
              path="developers/webhooks/endpoints/:endpoint_id"
              element={<EndpointDetailsPage />}
            />
            <Route path="developers/webhooks/events/:event_id" element={<EventDetailsPage />} />
            <Route path="credits" element={<CreditsPage />} />
            <Route path="configure/:page" element={<ConfigurePage />} />
            <Route path="configure" element={<Navigate to="/configure/enrollment" replace />} />
            <Route
              path="team"
              element={
                <PrivateRoute
                  redirectPath={"/login"}
                  auth={isAuthUserPages}
                  requiredRoles={highAccessRoles}
                  businessAccounts={businessAccountsWithRole}
                >
                  <TeamPage />
                </PrivateRoute>
              }
            />
            <Route path="digital-receipt" element={<DigitalReceiptPage />} />
            <Route path="business_settings/:hash/:id" element={<BusinessSettingsPage />} />
            <Route path="settings/:hash/:action" element={<PersonalSettingsPage balance />} />
            <Route path="connect-shopify/verify" element={<ConnectShopifyStoreVerifyPage />} />
            <Route
              element={<PrivateRoute redirectPath={defaultInnerPage} auth={isHighAccessRoleBA} />}
            >
              <Route path="health/overview" element={<HealthOverviewPage />} />
              <Route
                path="health/overview/chargeback_olympics"
                element={
                  <CountryOlympicsGuard>
                    <ChargebackOlympicsPage />
                  </CountryOlympicsGuard>
                }
              />
              <Route
                path="health/overview/breakdown_by_country"
                element={
                  <CountryOlympicsGuard>
                    <BreakdownByCountryPage />
                  </CountryOlympicsGuard>
                }
              />
              <Route
                path="health/overview/breakdown_by_reason_code"
                element={<BreakdownByReasonCodePage />}
              />
              <Route
                element={
                  <PrivateRoute
                    redirectPath="/dashboard"
                    auth={userInfo?.is_business_health_allowed}
                  />
                }
              >
                <Route path="health/monitoring" element={<HealthMonitoringPage />} />
                <Route
                  path="health/monitoring/estimated_program_status"
                  element={<EstimatedProgramStatusPage />}
                />
                <Route
                  path="health/monitoring/mastercard_estimated_program_status"
                  element={<MastercardEstimatedProgramStatusPage />}
                />
              </Route>
            </Route>
            <Route
              path="/getting_started"
              element={
                <PrivateRoute redirectPath="/dashboard" auth={isOpenOnboarding}>
                  <GettingStartedPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/getting_started/new-business"
              element={
                // <PrivateRoute redirectPath="/dashboard" auth={isOpenOnboarding} >
                <GettingStartedPage />
                // </PrivateRoute>
              }
            />
          </Route>
        </Route>

        {/* === ADMIN PAGES === */}
        <Route element={<PrivateRoute redirectPath="/login" auth={isAuthAdminPages} />}>
          <Route element={<CustomLayout />}>
            <Route
              path="admin/dashboard/alerts_by_country"
              element={
                <LayoutUserPageForAdmin>
                  <AlertsByCountryPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route path="dashboard/users_by_source" element={<UsersBySourcePage />} />
            <Route
              path="admin/dashboard/alerts_by_bank"
              element={
                <LayoutUserPageForAdmin>
                  <AlertsByBankPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/dashboard"
              element={
                <LayoutUserPageForAdmin>
                  <DashboardPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/alerts"
              element={
                <LayoutUserPageForAdmin>
                  <AlertsPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/alerts/details/:alert_id"
              element={
                <LayoutUserPageForAdmin>
                  <AlertDetailsPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/integrations"
              element={
                <LayoutUserPageForAdmin>
                  <IntegrationsPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route path="admin/perks" element={<PerksAdminPage />} />
            <Route path="admin/payment_matching" element={<PaymentMatchingPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route element={<PrivateRoute redirectPath="/users" auth={isSuperAdmin} />}>
              <Route path="admin_users" element={<AdminUsersPage />} />
            </Route>
            <Route path="users/user_balance" element={<UserBalancePageForAdmin />} />
            <Route path="business_accounts" element={<BusinessAccountsPage />} />
            <Route path="referral" element={<Referrals />} />
            <Route path="referral/affiliate/:id" element={<EarningsAdminPage />} />
            <Route path="referral/referral-ba/:id" element={<ReferralBAPage />} />
            <Route element={<PrivateRoute redirectPath="/alerts" auth={isAdmin} />}>
              <Route path="pricing" element={<PricingPage />} />
            </Route>
            <Route element={<PrivateRoute redirectPath="/alerts" auth={!isAdminPartner} />}>
              <Route path="invoices" element={<AdminInvoicesPage />} />
            </Route>
            <Route
              element={
                <PrivateRoute redirectPath="/alerts" auth={!isCommonAdmin && !isAdminPartner} />
              }
            >
              <Route path="marketing" element={<MarketingPage />} />
            </Route>
            <Route path="requests/:hash" element={<RequestsPage />} />
            <Route path="business_settings" element={<BusinessSettingsPage />}>
              <Route path="enrollment" element={<AdminEnrollments />} />
              <Route path="auto-refund" element={<AutoRefundPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="business" element={<Business />} />
              <Route path="users" element={<Users isBusinessSettingsPage />} />
              <Route path="business_health/overview" element={<HealthOverviewPage />} />
              <Route
                path="business_health/overview/chargeback_olympics"
                element={
                  <CountryOlympicsGuard>
                    <ChargebackOlympicsPage />
                  </CountryOlympicsGuard>
                }
              />
              <Route
                path="business_health/overview/breakdown_by_country"
                element={
                  <CountryOlympicsGuard>
                    <BreakdownByCountryPage />
                  </CountryOlympicsGuard>
                }
              />
              <Route
                path="business_health/overview/breakdown_by_reason_code"
                element={<BreakdownByReasonCodePage />}
              />
              <Route path="business_health/monitoring" element={<HealthMonitoringPage />} />
              <Route
                path="business_health/monitoring/estimated_program_status"
                element={<EstimatedProgramStatusPage />}
              />
              <Route
                path="business_health/monitoring/mastercard_estimated_program_status"
                element={<MastercardEstimatedProgramStatusPage />}
              />
            </Route>
          </Route>
        </Route>

        {/* === SHARED PAGES (USER OR ADMIN) === */}
        <Route
          element={
            <PrivateRoute redirectPath="/login" auth={isAuthStandardUser || isAuthAdminPages} />
          }
        >
          <Route element={<CustomLayout />}>
            <Route element={<PrivateRoute redirectPath="/alerts" auth={!isCommonAdmin} />}>
              <Route path="dashboard/alerts_by_country" element={<AlertsByCountryPage />} />
              <Route path="dashboard/alerts_by_bank" element={<AlertsByBankPage />} />
              <Route path="dashboard" element={<DashboardPage demoRef={demoRef} />} />
              <Route path="dashboard/thank-you" element={<DashboardPage demoRef={demoRef} />} />
            </Route>
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="alerts/details/:alert_id" element={<AlertDetailsPage />} />
            <Route
              element={<PrivateRoute redirectPath="/dashboard" auth={!!userInfo?.show_perks} />}
            >
              <Route path="perks" element={<PerksPage />} />
            </Route>
            <Route path="business_settings/:hash" element={<BusinessSettingsPage />} />
            <Route path="settings/:hash" element={<PersonalSettingsPage />} />
          </Route>
        </Route>

        <Route
          path="settings"
          element={<PrivateRoute redirectPath={"/settings/my_profile"} auth={false} />}
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <Modal
        {...(isMobile ? { transitionName: "" } : null)}
        open={!!connectPayment && userInfo?.new_onboarding_finished}
        centered={!isMobile}
        width={442}
        title={
          <Space direction="vertical">
            <Text className="payment-connect-title">
              {t("_namePayment_ connect", { namePayment: connectPaymentName })}
            </Text>
            <Text className="payment-connect-sub_title">
              {filteredBusinessAccounts?.length
                ? t(
                    "Which business account do you want to connect to your current _namePayment_ account?",
                    { namePayment: connectPaymentName }
                  )
                : businessAccountsFetching
                  ? ""
                  : `No available business accounts to connect ${connectPaymentName}.`}
            </Text>
          </Space>
        }
        footer={null}
        closeIcon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
        closable="true"
        onCancel={handleCancel}
        destroyOnClose
        loading={businessAccountsFetching}
        classNames={{
          content: "clear-bg full-width-header payment-connect",
          header: cls("payment-connect", [connectPayment?.["service_name"]])
        }}
      >
        <Spin
          size="large"
          //spinning={fetching}
          spinning={false}
          wrapperClassName="visible"
        >
          <ExternallyConnectPaymentProcessorForm
            onClose={handleCancel}
            onSubmit={handleSubmit}
            businessAccounts={filteredBusinessAccounts}
            businessAccountsFetching={businessAccountsFetching}
            error={saveResult?.["connect_error"]}
            paymentProcessorName={connectPaymentName}
            loading={saveFetching}
          />
        </Spin>
      </Modal>

      <AcceptInviteModal />
    </>
  )
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  isMobile: state.nav.isMobile,

  businessAccounts: state.user.businessAccounts,
  businessAccountsFetching: state.user.businessAccountsFetching,
  businessAccountsWithRole: state.user.businessAccountsWithRole,
  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult,
  demoTourStep: state.user.demoTourStep
})

const mapDispatchToProps = (dispatch) => ({
  connectPaymentProcessor: (data) => dispatch(UserActions.connectPaymentProcessorRequest(data)),
  disconnectPaymentProcessor: (data) =>
    dispatch(UserActions.disconnectPaymentProcessorExternallyRequest(data)),
  userSaveResultReset: () => dispatch(UserActions.userSaveResultReset()),

  changeNextCreateAccountStep: (nextCreateAccountStep) =>
    dispatch(UserActions.changeNextCreateAccountStep(nextCreateAccountStep)),
  setDemoTourStep: (step) => dispatch(UserActions.setDemoTourStep(step)),
  getMaxCreateSteps: () => dispatch(UserActions.getMaxCreateStepsRequest()),
  setRedirectAfterCreateAccount: (location) =>
    dispatch(UserActions.userSetProp("redirectAfterCreateAccount", location)),
  clearMessage: () => dispatch(AuthActions.updateProfile({ message: null })),
  logout: () => dispatch(AuthActions.logout())
})

InternalPages.propTypes = {
  auth: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    isValidRefreshToken: PropTypes.bool
  }),
  userInfo: PropTypes.shape({
    onboarding_finished: PropTypes.bool
  }),
  isAdmin: PropTypes.bool
}

export default connect(mapStateToProps, mapDispatchToProps)(InternalPages)
